import gsap from 'gsap';

/**
 * InfiniteVerticalSlider
 * A seamless, infinitely looping vertical slider for lists of cards.
 * Features:
 *  - Looping with perfect continuity using clones
 *  - Autoplay with delay on menu trigger hover
 *  - Pause/resume logic on slider hover
 *  - "Active" and "fade-out" class management for card state & effects
 */
export class InfiniteVerticalSlider {
  // ---- Default configuration constants (with units) ----
  static DEFAULT_CARD_HEIGHT = 96; // px: Height of a card
  static DEFAULT_GAP = 10; // px: Gap between cards
  static DEFAULT_ANIM = 0.8; // seconds: Slide animation duration
  static DEFAULT_AUTO = 2000; // ms: Interval between autoplay slides
  static DEFAULT_PAUSE_DELAY = 500; // ms: Delay before resuming after hover out
  static DEFAULT_BUTTON_DELAY = 200; // ms: How long trigger must be hovered before autoplay starts
  static STACKED_VISIBLE = 3; // Number of visible cards in the stack

  constructor( {
    trigger, // Menu, DOM element that controls autoplay
    wrap, // The slider container DOM element
    track, // The UL (track) element holding LI card items
    cardHeight = InfiniteVerticalSlider.DEFAULT_CARD_HEIGHT,
    gap = InfiniteVerticalSlider.DEFAULT_GAP,
    anim = InfiniteVerticalSlider.DEFAULT_ANIM,
    auto = InfiniteVerticalSlider.DEFAULT_AUTO,
    pauseDelay = InfiniteVerticalSlider.DEFAULT_PAUSE_DELAY,
    buttonDelay = InfiniteVerticalSlider.DEFAULT_BUTTON_DELAY
  } ) {
    // ----- Validate DOM references: abort if missing -----
    if (
      !( trigger instanceof HTMLElement ) ||
      !( wrap instanceof HTMLElement ) ||
      !( track instanceof HTMLElement )
    ) {
      console.warn( 'InfiniteVerticalSlider: missing or invalid elements.' );
      return;
    }

    // ----- Instance configuration -----
    this.trigger = trigger;
    this.wrap = wrap;
    this.track = track;
    this.STEP = cardHeight + gap; // px: One card+gap movement per slide
    this.ANIM = anim; // seconds: Slide tween duration
    this.AUTO = auto; // ms: Autoplay cadence
    this.PAUSE_DELAY = pauseDelay; // ms: After slider hover, wait this long before resuming
    this.BUTTON_DELAY = buttonDelay; // ms: Must hover/focus trigger this long to start autoplay
    this.VISIBLE_CARDS = InfiniteVerticalSlider.STACKED_VISIBLE;

    // ----- Track original (real) cards before cloning -----
    this.originals = [...this.track.children];

    // If only 1 card, just activate and skip all behaviors
    if ( this.originals.length <= 1 ) {
      this.originals[0]?.classList.add( 'active' );
      return;
    }

    // ----- Full setup -----
    this._setup();
  }

  /**
   * Main setup: clone cards for looping, assign data-ids,
   * initialize transform state, and attach events.
   */
  _setup() {
    // Assign a unique data-id to each original card for "active" tracking
    this.originals.map( ( li, i ) => {
      li.dataset.id = i;
      return li;
    } );

    // Prepend clones (reversed) and append clones (normal) for infinite loop
    this.originals
      .slice()
      .reverse()
      .map( li => {
        this.track.prepend( li.cloneNode( true ) );
        return li;
      } );
    this.originals.map( li => {
      this.track.appendChild( li.cloneNode( true ) );
      return li;
    } );

    // Set initial y-offset so the "center" set of cards is visible
    // (This prevents drift over time as we loop and keeps math simple)
    this.loopH = this.STEP * this.originals.length;
    gsap.set( this.track, { y: -this.loopH } );

    // Set the first "real" card as active
    this.setActive( this.getIndex( -this.loopH ) );

    // Set up timers for autoplay, resume, and hover
    this.interval = null;
    this.resumeTimer = null;
    this.buttonTimer = null;

    // Slide function memoized for setInterval
    this.slideRunner = () => this.slide();

    // Bind all event listeners for trigger and slider
    this.bindEventListeners();
  }

  /**
   * Returns the visible data-id for a given y offset.
   * Ensures we wrap around the original set for infinite effect.
   * @param {number} y - Current y transform (px)
   * @returns {number} data-id of the currently "active" card
   */
  getIndex( y ) {
    y = Number( y );
    const raw = Math.round( -y / this.STEP );
    const mod = raw % this.originals.length;
    // Always a positive number (so -1 => last card, etc)
    return mod < 0 ? mod + this.originals.length : mod;
  }

  /**
   * "Normalize" y: Keeps the slider's y position within the center set of cards.
   * This ensures a seamless infinite loop effect.
   * Whenever we go too far up or down (past either cloned region), snap y
   * back to the equivalent card in the middle block.
   * @param {number} y - Current y transform
   * @returns {number} Normalized y, still representing the same "visible" card
   */
  normalizeY( y ) {
    y = Number( y );
    // If we've scrolled up past two original sets, snap down
    if ( y <= -2 * this.loopH ) {
      return y + this.loopH;
    }
    // If we've scrolled down past the main set, snap up
    if ( y >= 0 ) {
      return y - this.loopH;
    }
    // Otherwise, already normalized
    return y;
  }

  /**
   * Apply .active class to all cards (and their clones) with given data-id.
   * @param {number} id
   */
  setActive( id ) {
    this.track
      .querySelectorAll( '.active' )
      .forEach( el => el.classList.remove( 'active' ) );
    this.track
      .querySelectorAll( `[data-id="${id}"]` )
      .forEach( el => el.classList.add( 'active' ) );
  }

  /**
   * Toggle .fade-out class on all cards with the given data-id.
   * @param {number} id
   * @param {boolean} on
   */
  fadeOut( id, on = true ) {
    this.track
      .querySelectorAll( `[data-id="${id}"]` )
      .forEach( el => el.classList.toggle( 'fade-out', on ) );
  }

  /**
   * Animate to the next card, update .active and .fade-out classes,
   * and perform seamless loop logic if needed.
   */
  slide() {
    // Current y value
    const from = Number( gsap.getProperty( this.track, 'y' ) );
    // Next y target (one card up)
    const to = from - this.STEP;

    // Previous and next card indices
    const prev = this.getIndex( from );
    const next = this.getIndex( to );

    // Fade out old card, activate new one
    this.fadeOut( prev, true );
    this.setActive( next );

    gsap.to( this.track, {
      y: to,
      duration: this.ANIM,
      ease: 'power2.out',
      overwrite: true,
      onComplete: () => {
        // --- Normalize y (if we reached a clone, snap to center block) ---
        const fixed = this.normalizeY( to );
        if ( fixed !== to ) {
          gsap.set( this.track, { y: fixed } );
        }

        // Remove fade-out class after animation completes
        this.track
          .querySelectorAll( '.fade-out' )
          .forEach( el => el.classList.remove( 'fade-out' ) );

        // Make sure .active is correctly set for current top card
        const nowY = Number( gsap.getProperty( this.track, 'y' ) );
        this.setActive( this.getIndex( nowY ) );
      }
    } );
  }

  /**
   * Start autoplay interval (optionally animates immediately).
   * @param {boolean} immediate - If true, animates one slide immediately.
   */
  startAuto( immediate = false ) {
    clearInterval( this.interval );
    clearTimeout( this.resumeTimer );

    if ( immediate ) {
      this.slideRunner();
    }

    this.interval = setInterval( this.slideRunner, this.AUTO );
  }

  /**
   * Stop autoplay and clear timers.
   */
  stopAuto() {
    clearInterval( this.interval );
    this.interval = null;
    clearTimeout( this.resumeTimer );
  }

  /**
   * Wire up all pointer/focus events for trigger and slider.
   * Handler names are semantic (not just "button enter").
   */
  bindEventListeners() {
    // Handle trigger hover/focus to start autoplay after delay
    const handleTriggerPointerEnter = () => {
      clearTimeout( this.buttonTimer );
      this.buttonTimer = setTimeout( () => this.startAuto( true ), this.BUTTON_DELAY );
    };

    // Handle trigger unhover/unfocus to stop autoplay immediately
    const handleTriggerPointerLeave = () => {
      clearTimeout( this.buttonTimer );
      this.stopAuto();
    };

    this.trigger.addEventListener( 'mouseenter', handleTriggerPointerEnter );
    this.trigger.addEventListener( 'focusin', handleTriggerPointerEnter );
    this.trigger.addEventListener( 'mouseleave', handleTriggerPointerLeave );
    this.trigger.addEventListener( 'focusout', handleTriggerPointerLeave );

    // Handle slider hover: pause autoplay; on mouse leave, resume after delay
    const handleSliderPointerLeave = () => {
      this.stopAuto();
      this.resumeTimer = setTimeout( () => this.startAuto( true ), this.PAUSE_DELAY );
    };

    this.wrap.addEventListener( 'mouseenter', () => this.stopAuto() );
    this.wrap.addEventListener( 'focusin', () => this.stopAuto() );
    this.wrap.addEventListener( 'mouseleave', handleSliderPointerLeave );
    this.wrap.addEventListener( 'focusout', handleSliderPointerLeave );
  }
}
